import {
	VIDEO_EXTENSIONS,
	VIDEO_QUALITY_SUFFIX_720,
	VIDEO_QUALITY_SUFFIX_1080,
	VIDEO_QUALITY_SUFFIXES,
} from "@shared/types/constants";

export type VideoQualityLabel = "720p" | "1080p";

export interface VideoQualityVariantKeys {
	"720p"?: string;
	"1080p"?: string;
}

// this will return the base key (without quality suffix) and extension
// for example "sample-720p.mp4" => { base: "sample", ext: ".mp4" }
//  we are using this to decide the base key for different quality variants
function getBaseKey(key: string): { base: string; ext: string } | null {
	const lastSlash = key.lastIndexOf("/");
	const basename = lastSlash >= 0 ? key.slice(lastSlash + 1) : key;
	const lastDot = basename.lastIndexOf(".");
	if (lastDot < 0) return null;
	const ext = basename.slice(lastDot);
	const nameWithoutExt = basename.slice(0, lastDot);
	const pathPrefix = lastSlash >= 0 ? key.slice(0, lastSlash + 1) : "";
	let baseName = nameWithoutExt;
	for (const suffix of VIDEO_QUALITY_SUFFIXES) {
		if (baseName.endsWith(suffix)) {
			baseName = baseName.slice(0, -suffix.length);
			break;
		}
	}
	return { base: pathPrefix + baseName, ext };
}

// this doesnt mean the variants exist, just constructs the keys
export function getQualityVariantKeys(key: string): VideoQualityVariantKeys {
	const parsed = getBaseKey(key);
	if (!parsed) return {};

	const { base, ext } = parsed;
	const result: VideoQualityVariantKeys = {};
	result["720p"] = `${base}${VIDEO_QUALITY_SUFFIX_720}${ext}`;
	result["1080p"] = `${base}${VIDEO_QUALITY_SUFFIX_1080}${ext}`;
	return result;
}

// returns only the existing variant keys in an array earlier one was returning an object
export function getAllQualityVariantKeys(key: string): string[] {
	const variants = getQualityVariantKeys(key);
	return [variants["720p"], variants["1080p"]].filter(
		(k): k is string => typeof k === "string",
	);
}


// it just return the name of the key before last hyphen, however getBaseKey(key) return thr object with base and extension
export function getBaseBeforeLastHyphen(key: string): string | null {
	const i = key.lastIndexOf("-");
	if (i < 0) return null;
	return key.slice(0, i);
}

// checks if the key is 720p variant
export function is720Variant(key: string): boolean {
	const basename = key.includes("/") ? key.slice(key.lastIndexOf("/") + 1) : key;
	const lastDot = basename.lastIndexOf(".");
	const nameWithoutExt = lastDot >= 0 ? basename.slice(0, lastDot) : basename;
	return nameWithoutExt.endsWith(VIDEO_QUALITY_SUFFIX_720);
}

// checks if the key is 1080p variant
export function is1080Variant(key: string): boolean {
	const basename = key.includes("/") ? key.slice(key.lastIndexOf("/") + 1) : key;
	const lastDot = basename.lastIndexOf(".");
	const nameWithoutExt = lastDot >= 0 ? basename.slice(0, lastDot) : basename;
	return nameWithoutExt.endsWith(VIDEO_QUALITY_SUFFIX_1080);
}


// converts suffix to label like "720p", "1080p" or "Quality 1", "Quality 2", etc.
function suffixToLabel(suffix: string, index: number): string {
	const match = /^(\d+)/.exec(suffix);
	if (match) return `${match[1]}p`;
	return `Quality ${index + 1}`;
}


// returns a mapping of quality labels to their corresponding keys from the list of all keys
// for example, for key "sample-720p.mp4" and list ["sample-720p.mp4", "sample-1080p.mp4"]
// it will return { "1080p": "sample-1080p.mp4", "720p": "sample-720p.mp4" }
export function getQualityVariantsFromList(
	key: string,
	allKeys: string[],
): Record<string, string> {
	const base = getBaseBeforeLastHyphen(key);
	if (!base) return {};

	const prefix = base + "-";
	const variants = allKeys.filter(
		(k) => k !== key && k.startsWith(prefix) && isVideoKey(k),
	);
	// Also include the clicked key if it has a hyphen (it's a variant)
	if (key.startsWith(prefix) && isVideoKey(key)) {
		if (!variants.includes(key)) variants.push(key);
	}
	if (variants.length === 0) return {};

	const withLabel = variants.map((k) => {
		const suffix = k.slice(prefix.length);
		const numMatch = /^(\d+)/.exec(suffix);
		const num = numMatch ? parseInt(numMatch[1], 10) : 0;
		return { key: k, label: suffixToLabel(suffix, 0), num };
	});
	withLabel.sort((a, b) => b.num - a.num);

	const seen = new Set<string>();
	const result: Record<string, string> = {};
	for (const { key: k, label } of withLabel) {
		let l = label;
		let n = 0;
		while (seen.has(l)) l = `${label} (${++n})`;
		seen.add(l);
		result[l] = k;
	}
	return result;
}



// checks if the key is a video file based on its extension
export function isVideoKey(key: string): boolean {
	const lower = key.toLowerCase();
	return VIDEO_EXTENSIONS.some((ext: string) => lower.endsWith(ext));
}


// groups video files by their base keys and selects representative videos for display
export function groupVideosForDisplay<T extends { key: string }>(files: T[]): T[] {
	const videoGroups = new Map<string, T[]>();
	const videoOrder: string[] = [];
	const nonVideos: T[] = [];

	for (const file of files) {
		if (!isVideoKey(file.key)) {
			nonVideos.push(file);
			continue;
		}
		const base = getBaseBeforeLastHyphen(file.key );
		if (!base) {
			nonVideos.push(file);
			continue;
		}
		if (!videoGroups.has(base)) {

			videoOrder.push(base);
		}
		if(isVideoKey(file.key) && !(is720Variant(file.key) || is1080Variant(file.key))) { // this for any video file which doesn't follow the naming
				nonVideos.push(file);
		}
		const list = videoGroups.get(base) ?? [];
		list.push(file);
		videoGroups.set(base, list);
	}

	const representativeVideos: T[] = [];
	for (const base of videoOrder) {
		const group = videoGroups.get(base)!;
		// Only show 720p in the list; 1080p is for backend/playback only
		const preferred720 = group.find((f) => is720Variant(f.key));
		if (preferred720) {
			representativeVideos.push(preferred720);
		}
		// If no 720p in group (e.g. only 1080p), omit from list
	}

	return [...nonVideos, ...representativeVideos];
}
